import React from 'react';
import { useTranslation } from 'react-i18next';
import { CatalogAttributeDefinition } from './catalogAttributes';
import styles from './Catalog.module.css';

type CatalogAttributeFiltersProps = {
  ns: 'products' | 'services';
  definitions: CatalogAttributeDefinition[];
  values: Record<string, string>;
  onChange: (attributeId: string, value: string) => void;
};

export const CatalogAttributeFilters: React.FC<CatalogAttributeFiltersProps> = ({
  ns,
  definitions,
  values,
  onChange,
}) => {
  const { t } = useTranslation([ns, 'common']);

  if (definitions.length === 0) {
    return null;
  }

  return (
    <div className={styles.attributeFilters}>
      {definitions.map(definition => {
        const fieldId = `catalog-filter-attr-${definition.id}`;
        const value = values[definition.id] ?? '';
        const emptyLabel = t('filterAnyValue', { ns, name: definition.name });
        if (definition.fieldType === 'select' || definition.fieldType === 'yesNo') {
          const options = definition.fieldType === 'yesNo'
            ? [
                { value: 'true', label: t('yes', { ns }) },
                { value: 'false', label: t('no', { ns }) },
              ]
            : definition.options.map(option => ({ value: option, label: option }));
          return (
            <div key={definition.id} className={styles.filterField}>
              <label className={styles.filterFieldLabel} htmlFor={fieldId}>{definition.name}</label>
              <select
                id={fieldId}
                className={styles.filterControl}
                value={value}
                onChange={event => onChange(definition.id, event.target.value)}
              >
                <option value="">{emptyLabel}</option>
                {options.map(option => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </div>
          );
        }

        return (
          <div key={definition.id} className={styles.filterField}>
            <label className={styles.filterFieldLabel} htmlFor={fieldId}>{definition.name}</label>
            <input
              id={fieldId}
              className={styles.filterControl}
              type={definition.fieldType === 'number' ? 'number' : definition.fieldType === 'date' ? 'date' : 'text'}
              value={value}
              placeholder={emptyLabel}
              onChange={event => onChange(definition.id, event.target.value)}
            />
          </div>
        );
      })}
    </div>
  );
};

export const formatAttributeSummary = (
  definitions: CatalogAttributeDefinition[],
  values: Record<string, string>,
  yesLabel = 'Yes',
  noLabel = 'No',
) => definitions
  .map(definition => {
    const text = (values[definition.id] || '').trim();
    if (!text) return '';
    if (definition.fieldType === 'yesNo') return text === 'true' ? yesLabel : noLabel;
    return text;
  })
  .filter(Boolean)
  .join(' · ');
